// src/components/LoginPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [userEmail, setUserEmail] = useState('');
    const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [shouldRender, setShouldRender] = useState(false);

    const navigate = useNavigate();
    const {
        signInWithEmail,
        verifyOTP,
        signInWithGoogle,
        resendOTP,
        validateEmail,
        user,
        isLoading: authLoading
    } = useAuth();
    const codeInputRefs = useRef([]);

    // ✅ TOUS LES HOOKS EN PREMIER - Gestion de la redirection sans flash
    useEffect(() => {
        if (!authLoading) {
            if (user) {
                // Utilisateur connecté → redirection immédiate sans affichage
                navigate('/dashboard', { replace: true });
            } else {
                // Pas d'utilisateur → autoriser l'affichage
                setShouldRender(true);
            }
        }
    }, [user, authLoading, navigate]);

    // Focus automatique sur le premier champ de code
    useEffect(() => {
        if (currentStep === 2) {
            setTimeout(() => {
                codeInputRefs.current[0]?.focus();
            }, 300);
        }
    }, [currentStep]);

    // ✅ LOGIQUE MÉTIER APRÈS LES HOOKS

    // Gestion de la connexion Google
    const handleGoogleLogin = async () => {
        setError('');
        setIsLoading(true);

        try {
            await signInWithGoogle();
            // La redirection sera gérée automatiquement par Supabase
        } catch (err) {
            console.error('Erreur Google login:', err);
            setError(err.message || 'Erreur lors de la connexion Google');
        } finally {
            setIsLoading(false);
        }
    };

    // Gestion du formulaire email
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setIsLoading(true);

        try {
            // Validation côté client
            validateEmail(userEmail);

            // Envoi du code via Supabase
            const result = await signInWithEmail(userEmail);

            if (result.success) {
                setSuccessMessage(`Code envoyé à ${userEmail}`);
                setCurrentStep(2);
            }

        } catch (err) {
            console.error('Erreur email submit:', err);
            setError(err.message || 'Erreur lors de l\'envoi du code');
        } finally {
            setIsLoading(false);
        }
    };

    // Gestion de la saisie des codes
    const handleCodeInput = (value, index) => {
        // Permettre seulement les chiffres
        if (!/^\d$/.test(value) && value !== '') return;

        const newCodeInputs = [...codeInputs];
        newCodeInputs[index] = value;
        setCodeInputs(newCodeInputs);

        // Passer au champ suivant automatiquement
        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }

        // Vérifier si tous les champs sont remplis
        if (newCodeInputs.every(code => code !== '') && newCodeInputs.join('').length === 6) {
            setTimeout(() => verifyCode(newCodeInputs.join('')), 500);
        }
    };

    // Gestion du copier-coller (strict)
    const handlePaste = (e, index) => {
        e.preventDefault();

        // Récupérer le texte collé
        const pastedText = e.clipboardData.getData('text').trim();

        // Validation stricte : uniquement des chiffres et maximum 6 caractères
        if (!/^\d+$/.test(pastedText) || pastedText.length > 6) {
            // Ne rien coller si ce n'est pas que des chiffres ou si plus de 6 caractères
            return;
        }

        const newCodeInputs = [...codeInputs];

        // Remplir les champs à partir de l'index actuel
        for (let i = 0; i < Math.min(pastedText.length, 6 - index); i++) {
            newCodeInputs[index + i] = pastedText[i];
        }

        setCodeInputs(newCodeInputs);

        // Focus sur le dernier champ rempli ou le suivant
        const nextIndex = Math.min(index + pastedText.length, 5);
        setTimeout(() => {
            codeInputRefs.current[nextIndex]?.focus();
        }, 0);

        // Auto-vérification si on a 6 chiffres
        if (newCodeInputs.every(code => code !== '') && newCodeInputs.join('').length === 6) {
            setTimeout(() => verifyCode(newCodeInputs.join('')), 500);
        }
    };

    // Gestion des touches pour les codes
    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !codeInputs[index] && index > 0) {
            const newCodeInputs = [...codeInputs];
            newCodeInputs[index - 1] = '';
            setCodeInputs(newCodeInputs);
            codeInputRefs.current[index - 1]?.focus();
        }

        if (e.key === 'ArrowLeft' && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }

        if (e.key === 'ArrowRight' && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    // Vérification du code via Supabase
    const verifyCode = async (enteredCode) => {
        setError('');
        setIsLoading(true);

        try {
            const result = await verifyOTP(userEmail, enteredCode);

            if (result.success) {
                setCurrentStep(3);
                setTimeout(() => {
                    navigate('/dashboard', { replace: true });
                }, 1500);
            }

        } catch (err) {
            console.error('Erreur verification:', err);
            setError(err.message || 'Code de vérification incorrect');

            // Réinitialiser les champs en cas d'erreur
            setCodeInputs(['', '', '', '', '', '']);
            setTimeout(() => {
                codeInputRefs.current[0]?.focus();
            }, 100);
        } finally {
            setIsLoading(false);
        }
    };

    // Renvoyer le code
    const handleResendCode = async () => {
        setError('');
        setSuccessMessage('');

        try {
            await resendOTP(userEmail);
            setSuccessMessage('Nouveau code envoyé !');
        } catch (err) {
            console.error('Erreur resend:', err);
            setError(err.message || 'Erreur lors du renvoi du code');
        }
    };

    // Retour en arrière
    const goBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
            setCodeInputs(['', '', '', '', '', '']);
            setError('');
            setSuccessMessage('');
            setIsLoading(false);
        }
    };

    // Afficher plus d'informations
    const showMoreInfo = () => {
        alert('Lamien FinAI - Plateforme d\'analyse financière alimentée par IA\n\n• Analyse de marché en temps réel\n• Screening d\'actions avancé\n• Alertes personnalisées\n• Confidentialité garantie');
    };

    // ✅ GESTION DU RENDU - Ne pas afficher pendant le loading ou si utilisateur connecté
    if (authLoading || (user && !shouldRender)) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-600">Vérification de la session...</p>
                </div>
            </div>
        );
    }

    // Si l'utilisateur est connecté, ne pas afficher la page
    if (user) {
        return null;
    }

    // Afficher la page seulement si autorisé
    if (!shouldRender) {
        return null;
    }

    // ✅ RENDU JSX PRINCIPAL
    return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-5 relative">
            {/* Bouton retour */}
            {currentStep === 2 && (
                <button
                    onClick={goBack}
                    className="absolute top-5 left-5 bg-transparent border-none text-gray-500 cursor-pointer text-sm p-2 rounded-md hover:bg-gray-200 hover:text-gray-700 transition-all duration-150"
                >
                    ← Retour
                </button>
            )}

            <div className="w-full max-w-lg flex flex-col items-center justify-center text-center">

                {/* Titre principal */}
                <div className="text-center mb-6 max-w-md">
                    <h1 className="text-4xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                        Bienvenue !
                    </h1>
                    <p className="text-gray-600 text-base leading-relaxed">
                        Accédez à votre agent conversationnel intelligent, spécialisé en finance traditionnelle, disponible 24/7.
                    </p>
                </div>

                {/* Messages de succès */}
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-600 text-sm max-w-sm w-full">
                        ✅ {successMessage}
                    </div>
                )}

                {/* Messages d'erreur */}
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm max-w-sm w-full">
                        ❌ {error}
                    </div>
                )}

                {/* Étape 1: Connexion/Inscription */}
                {currentStep === 1 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm w-full max-w-sm p-8 mb-6">
                        {/* Connexion Google */}
                        <button
                            onClick={handleGoogleLogin}
                            disabled={isLoading}
                            className="w-full p-3.5 bg-white border border-gray-300 rounded-md font-medium text-sm cursor-pointer transition-all duration-150 flex items-center justify-center gap-2.5 text-gray-700 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2"></div>
                                    Connexion...
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Continuer avec Google
                                </>
                            )}
                        </button>

                        <div className="relative text-center my-2 text-black text-xs font-medium">
                            <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200"></div>
                            <span className="bg-white px-3">OU</span>
                        </div>

                        {/* Formulaire Email */}
                        <form onSubmit={handleEmailSubmit}>
                            <input
                                type="email"
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                className="w-full p-3.5 border border-gray-300 rounded-md text-sm transition-all duration-150 bg-white text-gray-700 mb-4 focus:outline-none focus:border-blue-500 focus:shadow-sm focus:shadow-blue-100"
                                placeholder="Saisissez votre email personnel ou professionnel"
                                required
                                disabled={isLoading}
                            />

                            <button
                                type="submit"
                                disabled={isLoading || !userEmail.trim()}
                                className="w-full p-3.5 bg-gray-800 text-white border-none rounded-lg font-medium text-sm cursor-pointer transition-all duration-150 hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Envoi du code...' : 'Continuer avec l\'e-mail'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Étape 2: Vérification du code */}
                {currentStep === 2 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm w-full max-w-sm p-8 mb-6">
                        <div className="mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                Vérifiez votre email
                            </h2>
                            <p className="text-sm text-gray-600 mb-1">
                                Saisissez le code généré à partir du lien envoyé à
                            </p>
                            <p className="font-medium text-gray-900">{userEmail}</p>
                        </div>

                        <div className="mb-6">
                            <div className="flex justify-center gap-2 mb-6">
                                {codeInputs.map((value, index) => (
                                    <input
                                        key={index}
                                        ref={el => codeInputRefs.current[index] = el}
                                        type="text"
                                        value={value}
                                        onChange={(e) => handleCodeInput(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onPaste={(e) => handlePaste(e, index)}
                                        className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md bg-white text-gray-800 transition-all duration-150 focus:outline-none focus:border-blue-500 focus:shadow-sm focus:shadow-blue-100"
                                        maxLength="1"
                                        disabled={isLoading}
                                    />
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={() => verifyCode(codeInputs.join(''))}
                            disabled={isLoading || codeInputs.some(code => code === '')}
                            className="w-full p-3.5 bg-gray-800 text-white border-none rounded-md font-medium text-sm cursor-pointer transition-all duration-150 hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed mb-4"
                        >
                            {isLoading ? 'Vérification...' : 'Vérifier l\'adresse e-mail'}
                        </button>

                        <div className="text-center">
                            <p className="text-xs text-gray-600 mb-2">
                                Vous ne voyez pas l'e-mail dans votre boîte de réception ?
                            </p>
                            <button
                                onClick={handleResendCode}
                                disabled={isLoading}
                                className="text-blue-600 text-xs font-medium underline cursor-pointer transition-colors duration-150 hover:text-blue-700 disabled:opacity-50"
                            >
                                Essayez de renvoyer.
                            </button>
                        </div>
                    </div>
                )}

                {/* Étape 3: Succès */}
                {currentStep === 3 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm w-full max-w-sm p-8 mb-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg width="32" height="32" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path>
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                Authentification réussie !
                            </h2>
                            <p className="text-sm text-gray-600 mb-6">
                                Vous allez être redirigé vers le dashboard...
                            </p>
                            <div className="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                        </div>
                    </div>
                )}

                {/* Bouton "En savoir plus" */}
                {currentStep === 1 && (
                    <div>
                        <button
                            onClick={showMoreInfo}
                            className="bg-transparent border-none text-gray-400 text-xs cursor-pointer p-3 rounded-lg transition-all duration-150 flex items-center justify-center gap-1 hover:text-gray-600 hover:bg-gray-100"
                        >
                            En savoir plus
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginPage;